import React, { useState } from "react";
import {
  Box,
  Card,
  Container,
  Tab,
  Tabs,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useQuery } from "@tanstack/react-query";
import useBackend from "../../hooks/use_backend";
import { fetchHistoricalTerms } from "../../services/terms_service";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";

const DOC_TYPES = {
  PLATFORM: "platform",
  COMMISSIONING: "commissioning",
};

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`terms-tabpanel-${index}`}
      aria-labelledby={`terms-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function RichTextViewer({ rtfContent }) {
  const convertRtfToText = (rtf) => {
    if (!rtf) return "";
    const backslashPlaceholder = "\uE000";

    let text = rtf;

    // Remove RTF header and font table
    text = text.replace(/\{\\rtf1[^}]*\}/g, "");
    text = text.replace(/\{\\fonttbl[^}]*\}/g, "");
    text = text.replace(/\{\\colortbl[^}]*\}/g, "");
    text = text.replace(/\{\\stylesheet[^}]*\}/g, "");
    text = text.replace(/\{\\info[^}]*\}/g, "");

    // Convert RTF paragraph marks to double line breaks
    text = text.replace(/\\par\s*/g, "\n\n");
    text = text.replace(/\\line\s*/g, "\n");

    // Handle tabs
    text = text.replace(/\\tab\s*/g, "    ");

    // Remove other RTF control words but keep their parameters/text
    text = text.replace(/\\[a-z]+(-?\d+)?\s*/gi, "");

    // Remove braces
    text = text.replace(/[{}]/g, "");

    text = text.replace(/\\'/g, "'");
    text = text.replace(/\\\\/g, backslashPlaceholder);
    text = text.replace(new RegExp(backslashPlaceholder, "g"), "\\");

    // Clean up extra whitespace
    text = text.replace(/\n{3,}/g, "\n\n");
    text = text.trim();

    return text;
  };

  const formattedText = convertRtfToText(rtfContent);

  return (
    <Paper sx={{ p: 3, mt: 2, backgroundColor: "#f5f5f5", minHeight: "200px", maxHeight: "500px", overflow: "auto" }}>
      {formattedText.split("\n\n").map((paragraph, index) => (
        <Typography key={index} variant="body1" paragraph sx={{ mb: paragraph.trim() === "" ? 0 : 2 }}>
          {paragraph}
        </Typography>
      ))}
    </Paper>
  );
}

function TermsVersionsList({ versions, selectedVersion, onSelectVersion }) {
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const isActive = (fromDate, toDate) => {
    const now = new Date();
    const from = new Date(fromDate);
    const to = new Date(toDate);
    return now >= from && now <= to;
  };

  const handleDownload = (version, event) => {
    event.stopPropagation();
    if (version.PDF_URL) {
      window.open(version.PDF_URL, "_blank");
    }
  };

  return (
    <List sx={{ width: "100%" }}>
      {versions.map((version, index) => (
        <React.Fragment key={version.ID}>
          <ListItem
            secondaryAction={
              <IconButton
                edge="end"
                aria-label="download"
                onClick={(e) => handleDownload(version, e)}
                disabled={!version.PDF_URL}
              >
                <DownloadIcon />
              </IconButton>
            }
            disablePadding
          >
            <ListItemButton selected={selectedVersion?.ID === version.ID} onClick={() => onSelectVersion(version)}>
              <ListItemText
                primary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="subtitle1">
                      {version.DOC_TITLE} (v{version.DOC_VERSION})
                    </Typography>
                    {isActive(version.FROM_DATE, version.TO_DATE) && (
                      <Typography
                        variant="caption"
                        sx={{
                          backgroundColor: "success.main",
                          color: "white",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                        }}
                      >
                        ACTIVE
                      </Typography>
                    )}
                  </Box>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    Valid from {formatDate(version.FROM_DATE)} to {formatDate(version.TO_DATE)}
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
          {index < versions.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </List>
  );
}

export default function TermsAndConditions() {
  const [tabValue, setTabValue] = useState(0);
  const [selectedPlatformVersion, setSelectedPlatformVersion] = useState(null);
  const [selectedCommissioningVersion, setSelectedCommissioningVersion] = useState(null);
  const backend = useBackend();

  const {
    data: platformTerms,
    isLoading: isPlatformLoading,
    error: platformError,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.TERMS_PLATFORM, backend.fetch],
    queryFn: () => fetchHistoricalTerms(backend.fetch, DOC_TYPES.PLATFORM),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const {
    data: commissioningTerms,
    isLoading: isCommissioningLoading,
    error: commissioningError,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.TERMS_COMMISSIONING, backend.fetch],
    queryFn: () => fetchHistoricalTerms(backend.fetch, DOC_TYPES.COMMISSIONING),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Set default selected version when data loads (select the active one)
  React.useEffect(() => {
    if (platformTerms && !selectedPlatformVersion) {
      const now = new Date();
      const active = platformTerms.find((term) => {
        const from = new Date(term.FROM_DATE);
        const to = new Date(term.TO_DATE);
        return now >= from && now <= to;
      });
      setSelectedPlatformVersion(active || platformTerms[0]);
    }
  }, [platformTerms, selectedPlatformVersion]);

  React.useEffect(() => {
    if (commissioningTerms && !selectedCommissioningVersion) {
      const now = new Date();
      const active = commissioningTerms.find((term) => {
        const from = new Date(term.FROM_DATE);
        const to = new Date(term.TO_DATE);
        return now >= from && now <= to;
      });
      setSelectedCommissioningVersion(active || commissioningTerms[0]);
    }
  }, [commissioningTerms, selectedCommissioningVersion]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Terms & Conditions
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        View and download historical Terms & Conditions documents
      </Typography>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="terms tabs">
            <Tab label="Platform" id="terms-tab-0" />
            <Tab label="Commissioning" id="terms-tab-1" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {isPlatformLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {platformError && <Alert severity="error">Error loading platform terms: {platformError.message}</Alert>}
          {platformTerms && platformTerms.length > 0 ? (
            <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", md: "row" } }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Available Versions
                </Typography>
                <TermsVersionsList
                  versions={platformTerms}
                  selectedVersion={selectedPlatformVersion}
                  onSelectVersion={setSelectedPlatformVersion}
                />
              </Box>
              <Box sx={{ flex: 2 }}>
                {selectedPlatformVersion && (
                  <>
                    <Typography variant="h6" gutterBottom>
                      Preview: {selectedPlatformVersion.DOC_TITLE}
                    </Typography>
                    <RichTextViewer rtfContent={selectedPlatformVersion.DOC_TEXT_RTF} />
                  </>
                )}
              </Box>
            </Box>
          ) : (
            !isPlatformLoading && <Alert severity="info">No platform Terms & Conditions available</Alert>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {isCommissioningLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {commissioningError && (
            <Alert severity="error">Error loading commissioning terms: {commissioningError.message}</Alert>
          )}
          {commissioningTerms && commissioningTerms.length > 0 ? (
            <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", md: "row" } }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Available Versions
                </Typography>
                <TermsVersionsList
                  versions={commissioningTerms}
                  selectedVersion={selectedCommissioningVersion}
                  onSelectVersion={setSelectedCommissioningVersion}
                />
              </Box>
              <Box sx={{ flex: 2 }}>
                {selectedCommissioningVersion && (
                  <>
                    <Typography variant="h6" gutterBottom>
                      Preview: {selectedCommissioningVersion.DOC_TITLE}
                    </Typography>
                    <RichTextViewer rtfContent={selectedCommissioningVersion.DOC_TEXT_RTF} />
                  </>
                )}
              </Box>
            </Box>
          ) : (
            !isCommissioningLoading && <Alert severity="info">No commissioning Terms & Conditions available</Alert>
          )}
        </TabPanel>
      </Card>
    </Container>
  );
}
